import type { Rubro } from '../negocio';
import { PIEZAS, type Pieza } from './piezas';

/**
 * El escudo quieto, en el color del rubro: el de la barra lateral.
 *
 * Hace lo mismo que el POS (`app_logo_mark.dart`): **la llama toma el color del
 * negocio y, fuera del restaurante, pierde el gorro de cocinero**. Antes el
 * almacén ponía `lucqa-mark.svg` tal cual, violeta y con gorro, también en la
 * botica verde y en la ferretería azul: parecía el programa de otro.
 *
 * Se arma con las mismas piezas que el escudo animado (`piezas.ts`), con su
 * violeta y su lavanda cambiados por las variables de la marca. Por `style` y
 * no por el atributo `fill`, que no entiende `var()`.
 */
export function Isotipo({ rubro, className = '' }: { rubro: Rubro; className?: string }) {
  const conGorro = rubro === 'restaurant';

  return (
    <svg
      viewBox={`${VIEW_X} ${VIEW_Y} ${VIEW_ANCHO} ${VIEW_ALTO}`}
      className={className}
      role="img"
      aria-label="Lucqa"
    >
      <PiezaTenida nombre="fuego" />
      {conGorro ? null : <ParcheDelGorro />}
      <PiezaTenida nombre="cuerpo" />
      <PiezaTenida nombre="brazos" />
      {conGorro ? <PiezaTenida nombre="gorro" /> : null}
      <PiezaTenida nombre="ojo-izquierdo" />
      <PiezaTenida nombre="ojo-derecho" />
      <PiezaTenida nombre="hocico" />
      <PiezaTenida nombre="bigote" />
    </svg>
  );
}

/** La pieza con el violeta y la lavanda del dibujo pasados a la marca. */
export function piezaEnLaMarca(marcado: string): string {
  return marcado
    .replaceAll('fill="#6428f5"', 'style="fill: var(--marca)"')
    .replaceAll('fill="#d5bbfb"', 'style="fill: var(--marca-acento)"');
}

function PiezaTenida({ nombre }: { nombre: Pieza }) {
  // El marcado sale de los archivos del dibujo, generado y sin datos de nadie:
  // no hay nada que escapar.
  return <g dangerouslySetInnerHTML={{ __html: piezaEnLaMarca(PIEZAS[nombre]) }} />;
}

/**
 * El hueco del gorro, tapado: la llama trae recortada su silueta, y sin gorro
 * quedaba un gorro fantasma del color de la pared. Los mismos dos trozos que
 * mide el POS (`_PintorDelParche`), en las unidades del dibujo.
 */
function ParcheDelGorro() {
  return (
    <g style={{ fill: 'var(--marca)' }}>
      {TROZOS_DEL_GORRO.map(([izquierda, arriba, derecha, abajo]) => (
        <rect
          key={`${izquierda}-${arriba}`}
          x={izquierda}
          y={arriba}
          width={derecha - izquierda}
          height={abajo - arriba}
          rx={RADIO_DEL_PARCHE}
        />
      ))}
    </g>
  );
}

const TROZOS_DEL_GORRO: readonly (readonly [number, number, number, number])[] = [
  [284, 354, 414, 430],
  [258, 382, 416, 476],
];

// El 5 % del ancho, como el radio del POS.
const RADIO_DEL_PARCHE = 409.85 * 0.05;

const VIEW_X = 138.57;
const VIEW_Y = 219.38;
const VIEW_ANCHO = 409.85;
const VIEW_ALTO = 503.49;
